if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          this.updateFinalSaleInputs(this.currentVariant);
          this.updatePurchaseButtons(this.currentVariant, this.currentVariant.available);
        }

        this.filterSizesByColor();
        this.updateSiblingAvailability();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      filterSizesByColor() {
        const variantsJson = this.querySelector('script[data-product-variants]');
        if (!variantsJson) return;

        const raw = variantsJson.textContent ? variantsJson.textContent.trim() : '';
        if (!raw) return;

        let variants;
        try {
          variants = JSON.parse(raw);
        } catch (e) {
          console.error('Failed to parse product variants JSON', e);
          return;
        }

        const colorFieldset = this.querySelector('[data-option-name="color"]');
        const sizeFieldset = this.querySelector('[data-option-name="size"]');

        if (!colorFieldset || !sizeFieldset) return;

        const colorIndex = parseInt(colorFieldset.dataset.optionPosition, 10) - 1;
        const sizeIndex = parseInt(sizeFieldset.dataset.optionPosition, 10) - 1;
        const selectedColorInput = colorFieldset.querySelector('input:checked');
        const selectedColor = selectedColorInput ? selectedColorInput.value : null;

        if (!selectedColor || Number.isNaN(colorIndex) || Number.isNaN(sizeIndex)) return;

        const validSizes = new Set(
          variants
            .filter(
              (variant) =>
                variant.available &&
                variant.options &&
                variant.options[colorIndex] === selectedColor
            )
            .map((variant) => variant.options[sizeIndex])
        );

        const selectedSizeInput = sizeFieldset.querySelector('input[type="radio"]:checked');
        const selectedSize = selectedSizeInput ? selectedSizeInput.value : null;

        sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const label = sizeFieldset.querySelector(`label[for="${input.id}"]`);
          // Keep the selected size visible even when OOS (deep-link / Back in Stock).
          const keepVisible = validSizes.has(input.value) || input.value === selectedSize;

          if (keepVisible) {
            input.classList.remove('hidden');
            if (label) label.classList.remove('hidden');
          } else {
            input.classList.add('hidden');
            if (label) label.classList.add('hidden');
          }
        });

        // Only auto-switch when nothing is selected, not when the selection is OOS.
        if (!selectedSizeInput) {
          const firstValidInput = sizeFieldset.querySelector('input[type="radio"]:not(.hidden)');
          if (firstValidInput) {
            firstValidInput.checked = true;
            firstValidInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      updateSiblingAvailability() {
        // Hide sibling colours that have no in-stock sizes at all (not size-specific OOS).
        this.querySelectorAll('[data-sibling-product]').forEach((input) => {
          let variants = [];
          try {
            variants = JSON.parse(input.dataset.variants || '[]');
          } catch (e) {
            return;
          }

          const siblingColor = input.value;
          const hasAnyAvailable = variants.some(
            (variant) => variant.available && variant.options && variant.options.includes(siblingColor)
          );

          const label = this.querySelector(`label[for="${input.id}"]`);

          if (hasAnyAvailable) {
            input.classList.remove('hidden', 'disabled');
            input.removeAttribute('aria-disabled');
            if (label) label.classList.remove('hidden', 'disabled');
          } else {
            input.classList.add('hidden');
            input.classList.remove('disabled');
            input.removeAttribute('aria-disabled');
            if (label) {
              label.classList.add('hidden');
              label.classList.remove('disabled');
            }
          }
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const isSibling = target.dataset.siblingProduct === 'true';
        const variantId = target.dataset.variantId;
        if (isSibling && variantId) {
          const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
          this.switchSibling(productUrl, variantId);
          return;
        }

        // Optimistically filter sizes on the current DOM so the UI responds
        // immediately while the updated product info is being fetched.
        this.filterSizesByColor();
        this.disableButtons();
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        const requestUrl = this.buildRequestUrlWithParams(
          productUrl,
          selectedOptionValues,
          shouldFetchFullPage
        );

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      switchSibling(productUrl, variantId) {
        this.disableButtons();
        const viewMode = this.dataset.viewMode || 'main-product';
        // Fetch the section (not the full page) so we can patch in place and keep the swatch row
        // stable rather than replacing the whole <product-info>.
        const requestUrl = this.buildRequestUrlWithParams(productUrl, [], false, variantId);
        const focusedId = document.activeElement?.id || '';

        this.renderProductInfo({
          requestUrl,
          targetId: focusedId,
          callback: this.handleSiblingUpdate(productUrl, viewMode),
        });
      }

      handleSiblingUpdate(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          // Point the element at the swapped-in product's url so a later option change fetches it.
          this.dataset.url = productUrl;

          // Patch the product-specific regions in place — no teardown of the rest of product-info.
          const patch = (id) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.replaceChildren(...Array.from(source.childNodes).map((n) => n.cloneNode(true)));
            }
          };
          ['price', 'Sku', 'Inventory', 'Badges', 'PricePerItem', 'Volume', 'FinalSaleNotice', 'QuantityRules', 'VolumeNote'].forEach(patch);

          const newSelects = html.querySelector('variant-selects');
          if (newSelects) {
            // Replace the colour row wholesale from the fetched section. It renders in a stable
            // shared order, so this keeps the visible order identical while guaranteeing correct
            // ids, label adjacency, swatch backgrounds and checked state. An in-place attribute
            // rewire progressively desyncs `label[for]` from DOM order across swaps, which breaks
            // the `:checked + label` selected outline (it lands on whichever label follows the
            // checked input rather than the selected colour's own).
            const oldColor = this.querySelector('[data-option-name="color"]');
            const newColor = newSelects.querySelector('[data-option-name="color"]');
            if (oldColor && newColor) {
              const colorWasFocused = oldColor.contains(document.activeElement);
              oldColor.replaceChildren(...Array.from(newColor.childNodes).map((node) => node.cloneNode(true)));
              if (colorWasFocused) oldColor.querySelector('input:checked')?.focus();
            }

            // Size options belong to the new product — replace the size fieldset's contents.
            const oldSize = this.querySelector('[data-option-name="size"]');
            const newSize = newSelects.querySelector('[data-option-name="size"]');
            if (oldSize && newSize) {
              oldSize.replaceChildren(...Array.from(newSize.childNodes).map((n) => n.cloneNode(true)));
            }

            // Keep the embedded selected-variant JSON in sync for downstream reads.
            const oldSelected = this.querySelector('[data-selected-variant]');
            const newSelected = newSelects.querySelector('[data-selected-variant]');
            if (oldSelected && newSelected) {
              oldSelected.textContent = newSelected.textContent;
            }

            // Swap in the new product's full variants list so filterSizesByColor reads the right data.
            const oldVariants = this.querySelector('script[data-product-variants]');
            const newVariants = html.querySelector('script[data-product-variants]');
            if (oldVariants && newVariants) {
              oldVariants.textContent = newVariants.textContent;
            }
          }

          // Media is a different product's gallery — swap it with a short crossfade.
          this.swapSiblingMedia(html, variant);

          this.currentVariant = variant;
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl?.(variant?.id);
          this.updateVariantInputs(variant?.id);
          this.pickupAvailability?.update(variant);
          this.updatePurchaseButtons(variant, variant?.available ?? false);
          this.updateFinalSaleInputs(variant);
          this.filterSizesByColor();
          this.updateSiblingAvailability();
          this.refreshStickyAtcBar(productUrl);

          if (!variant) {
            this.setUnavailable();
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
          document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));

          this.enableButtons();
        };
      }

      refreshStickyAtcBar(productUrl) {
        const current = document.getElementById('shopify-section-sticky-atc-bar');
        if (!current) return;
        this.stickyAbortController?.abort();
        this.stickyAbortController = new AbortController();
        fetch(`${productUrl}?section_id=sticky-atc-bar`, { signal: this.stickyAbortController.signal })
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((text) => {
            const fresh = new DOMParser()
              .parseFromString(text, 'text/html')
              .getElementById('shopify-section-sticky-atc-bar');
            if (!fresh) return;
            // Pin the fresh bar's product id to the main product-info (which we deliberately don't
            // mutate) so its quantity-sync observer still resolves the main product without throwing.
            const mainId = this.getAttribute('data-product-id');
            if (mainId) {
              fresh.querySelectorAll('[data-product-id]').forEach((el) => el.setAttribute('data-product-id', mainId));
            }
            HTMLUpdateUtility.viewTransition(current, fresh, this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
          })
          .catch(() => {});
      }

      swapSiblingMedia(html, variant) {
        const newGallery = html.querySelector(`[id^="MediaGallery-${this.sectionId}"]`);
        const oldGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!newGallery || !oldGallery) {
          this.updateMedia(variant);
          return;
        }
        // Replace the element so the new media-gallery custom element self-initialises on insertion,
        // and fade it in. Preload the incoming primary image first so it renders from cache on swap
        // rather than fading in to a blank then popping — the source of the residual flash.
        const imported = document.importNode(newGallery, true);
        imported.id = oldGallery.id; // keep the queried id so later swaps still find the gallery (quick-view/featured use a different section id)
        const swap = () => {
          imported.style.opacity = '0';
          imported.style.transition = 'opacity 200ms ease';
          oldGallery.replaceWith(imported);
          void imported.offsetHeight; // force reflow so the opacity transition fires reliably
          imported.style.opacity = '1';
        };

        const primary = imported.querySelector('img');
        const src = primary?.getAttribute('src');
        if (!src) {
          swap();
          return;
        }

        const preloader = new Image();
        let swapped = false;
        const run = () => {
          if (swapped) return;
          swapped = true;
          preloader.onload = null;
          preloader.onerror = null;
          swap();
        };
        preloader.onload = run;
        preloader.onerror = run;
        const sizes = primary.getAttribute('sizes');
        const srcset = primary.getAttribute('srcset');
        if (sizes) preloader.sizes = sizes;
        if (srcset) preloader.srcset = srcset;
        preloader.src = src;
        if (preloader.complete) run();
        setTimeout(run, 500); // safety: never block the swap on a slow or missing image
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.filterSizesByColor();
          this.updateSiblingAvailability();
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.replaceChildren(
                ...Array.from(source.childNodes).map((n) => n.cloneNode(true))
              );
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('FinalSaleNotice');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.updatePurchaseButtons(variant, variant?.available ?? false);

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          this.updateFinalSaleInputs(variant);

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );

          this.enableButtons();
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const params = [];

        if (variantId) {
          params.push(`variant=${encodeURIComponent(variantId)}`);
        } else if (optionValues.length) {
          params.push(
            `option_values=${optionValues.map((v) => encodeURIComponent(v)).join(',')}`
          );
        }

        if (!shouldFetchFullPage) {
          params.push(`section_id=${encodeURIComponent(this.sectionId)}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // restore focus by id (getElementById tolerates special characters in Shopify ids)
            if (targetId) {
              document.getElementById(targetId)?.focus();
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateFinalSaleInputs(variant) {
        if (!this.productId) return;
        const forms = document.querySelectorAll(
          `form[is="product-form"][data-product-id="${this.productId}"]`
        );
        const showFinalSale =
          variant &&
          variant.compare_at_price != null &&
          parseFloat(variant.compare_at_price) > parseFloat(variant.price);

        forms.forEach((form) => {
          if (form.getAttribute('data-product-final-sale') !== 'true') return;

          const existing = form.querySelector('input[name="properties[Final sale]"]');
          const value =
            form.getAttribute('data-final-sale-value') ||
            'This item cannot be returned or exchanged.';

          if (showFinalSale) {
            if (!existing) {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = 'properties[Final sale]';
              input.value = value;
              form.appendChild(input);
            } else {
              existing.value = value;
            }
          } else if (existing) {
            existing.remove();
          }
        });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const variant = this.currentVariant;
        if (variant) {
          this.updatePurchaseButtons(variant, variant.available);
          return;
        }

        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        const variantInput = productForm.querySelector('[name="id"]');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (variantInput) variantInput.disabled = true;
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute('disabled');
          if (variantInput) variantInput.disabled = false;
          addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }

        if (!modifyClass) return;
      }

      updatePurchaseButtons(variant, isAvailable) {
        if (!this.productId) return;

        document
          .querySelectorAll(`form[is="product-form"][data-product-id="${this.productId}"]`)
          .forEach((form) => {
            const atcButton = form.querySelector('.product-form__atc-btn, [name="add"]');
            const bisButton = form.querySelector('.klaviyo-bis-trigger');
            const dynamicCheckout = form.querySelector('.product-form__dynamic-checkout, .shopify-payment-button');
            const variantInput = form.querySelector('[name="id"]');

            if (variantInput && variant) {
              variantInput.disabled = !isAvailable;
            }

            if (atcButton) {
              atcButton.classList.toggle('hidden', !isAvailable);
              if (isAvailable) {
                atcButton.removeAttribute('disabled');
              } else {
                atcButton.setAttribute('disabled', 'disabled');
              }
            }

            if (bisButton) {
              bisButton.classList.toggle('hidden', isAvailable);
            }

            if (dynamicCheckout) {
              dynamicCheckout.classList.toggle('hidden', !isAvailable);
            }
          });

        this.scheduleKlaviyoBackInStockRefresh(variant);
      }

      scheduleKlaviyoBackInStockRefresh(variant) {
        if (!variant) return;

        const refresh = () => this.refreshKlaviyoBackInStock(variant);
        refresh();
        requestAnimationFrame(refresh);
        setTimeout(refresh, 500);
      }

      refreshKlaviyoBackInStock(variant) {
        if (!variant || !this.productId) return;

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            bubbles: true,
            detail: { variant },
          })
        );
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      disableButtons() {
        this.toggleButtons(true);
      }

      enableButtons() {
        this.clearButtonLoadingState();
      }

      clearButtonLoadingState() {
        const buttonSelectors = [
          '[name="add"]',
          '.shopify-payment-button__button',
          '.sticky-atc-bar [name="add"]',
        ];

        buttonSelectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((button) => {
            button.style.pointerEvents = '';
            button.style.opacity = '';
          });
        });
      }

      toggleButtons(disabled) {
        const buttonSelectors = [
          '[name="add"]',
          '.shopify-payment-button__button',
          '.sticky-atc-bar [name="add"]',
        ];

        buttonSelectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((button) => {
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              button.style.pointerEvents = 'none';
              button.style.opacity = '0.6';
            } else {
              button.style.pointerEvents = '';
              button.style.opacity = '';
            }
          });
        });
      }
    }
  );
}
